import React, { useState, useEffect } from 'react';
import { useComments, useReviewLinks } from '../../hooks/useAdminData';
import StarRating from './StarRating';
import { FaGoogle } from 'react-icons/fa';
import { SiNextdoor } from 'react-icons/si';
import './Comments.scss';

// Type assertion for React Icons
const GoogleIcon = FaGoogle as React.ComponentType<{ className?: string }>;
const NextdoorIcon = SiNextdoor as React.ComponentType<{ className?: string }>;

const Comments: React.FC = () => {
  const comments = useComments();
  const reviewLinks = useReviewLinks();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [cardsPerView, setCardsPerView] = useState(3); // Default to 3 cards
  const [parallaxOffset, setParallaxOffset] = useState(0);
  const [expandedComments, setExpandedComments] = useState<Set<string>>(new Set());
  const [isAutoPlayPaused, setIsAutoPlayPaused] = useState(false);

  // Parallax effect
  useEffect(() => {
    const handleScroll = () => {
      const section = document.getElementById('comments');
      if (!section) return;

      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const sectionRect = section.getBoundingClientRect();
      const sectionTop = sectionRect.top + scrollTop;
      const sectionCenter = sectionTop + sectionRect.height / 2;
      const windowCenter = scrollTop + window.innerHeight / 2;
      
      // Calculate distance from section center to window center
      const distance = windowCenter - sectionCenter;
      
      // Parallax: background moves at 30% of scroll speed
      const parallaxSpeed = 0.3;
      const offset = distance * parallaxSpeed;
      
      setParallaxOffset(offset);
    };

    // Optimized scroll handler
    let ticking = false;
    const onScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', handleScroll, { passive: true });
    handleScroll(); // Initial call
    
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, []);

  // Responsive cards per view - ensure 3 cards on desktop
  useEffect(() => {
    const updateCardsPerView = () => {
      const width = window.innerWidth;
      if (width < 768) {
        setCardsPerView(1);
      } else if (width < 900) {
        setCardsPerView(2);
      } else {
        // Desktop: always show 3 cards per slide
        setCardsPerView(3);
      }
    };

    // Set initial value immediately
    const width = window.innerWidth;
    if (width < 768) {
      setCardsPerView(1);
    } else if (width < 900) {
      setCardsPerView(2);
    } else {
      // Desktop: always show 3 cards per slide
      setCardsPerView(3);
    }

    window.addEventListener('resize', updateCardsPerView);
    return () => window.removeEventListener('resize', updateCardsPerView);
  }, []);

  // Format date helper
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return 'Today';
    } else if (diffDays === 1) {
      return '1 day ago';
    } else if (diffDays < 7) {
      return `${diffDays} days ago`;
    } else if (diffDays < 14) {
      return '1 week ago';
    } else if (diffDays < 30) {
      const weeks = Math.floor(diffDays / 7);
      return `${weeks} week${weeks > 1 ? 's' : ''} ago`;
    } else if (diffDays < 365) {
      const months = Math.floor(diffDays / 30);
      return `${months} month${months > 1 ? 's' : ''} ago`;
    } else {
      return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    }
  };

  // Group comments into slides - always 3 cards per slide on desktop
  const totalSlides = Math.ceil(comments.length / cardsPerView);

  const getPlatformLogo = (platform: string) => {
    if (platform === 'google') {
      return <GoogleIcon className="platform-logo platform-logo-google" />;
    } else if (platform === 'nextdoor') {
      return <NextdoorIcon className="platform-logo platform-logo-nextdoor" />;
    }
    return null;
  };

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
    // Pause auto-play when user manually changes slide
    setIsAutoPlayPaused(true);
    // Resume auto-play after 10 seconds of inactivity
    setTimeout(() => {
      setIsAutoPlayPaused(false);
    }, 10000);
  };

  // Auto-play carousel
  useEffect(() => {
    if (totalSlides <= 1 || isAutoPlayPaused) return; // Don't auto-play if there's only one slide or if paused

    const interval = setInterval(() => {
      setCurrentSlide((prev) => {
        // Loop back to first slide after the last one
        return (prev + 1) % totalSlides;
      });
    }, 5000); // Change slide every 5 seconds

    return () => clearInterval(interval);
  }, [totalSlides, isAutoPlayPaused]);

  // Check if text is long enough to need "Read more"
  const isLongText = (text: string): boolean => {
    return text.length > 200;
  };

  // Toggle expanded state for a comment
  const toggleCommentExpanded = (commentId: string) => {
    setExpandedComments(prev => {
      const newSet = new Set(prev);
      if (newSet.has(commentId)) {
        newSet.delete(commentId);
      } else {
        newSet.add(commentId);
      }
      return newSet;
    });
  };

  // Get truncated text
  const getDisplayText = (text: string, commentId: string): string => {
    const isExpanded = expandedComments.has(commentId);
    if (isExpanded || !isLongText(text)) {
      return text;
    }
    // Return first 200 characters + ellipsis
    return text.substring(0, 200).trim() + '...';
  };

  if (comments.length === 0) {
    return null;
  }

  return (
    <section id="comments" className="comments">
      <div 
        className="comments-background"
        style={{
          transform: `translate3d(0, ${parallaxOffset}px, 0)`,
          backgroundImage: 'url(/assets/images/photo-degrade.webp)',
        }}
      ></div>
      <div className="comments-overlay"></div>
      <div className="container">
        <div className="comments-header">
          <h2 className="comments-title">
            WHAT OUR <span className="highlight-text">CLIENTS SAY</span>
          </h2>
          <p className="comments-subtitle">
            Don't just take our word for it - see what our happy customers have to say about their experience!
          </p>
        </div>

        <div className="carousel-container">
          <div className="carousel-wrapper">
            <div
              className="carousel-slides"
              style={{
                transform: `translateX(-${currentSlide * 100}%)`,
              }}
            >
              {Array.from({ length: totalSlides }).map((_, slideIndex) => {
                const startIndex = slideIndex * cardsPerView;
                const endIndex = startIndex + cardsPerView;
                const slideComments = comments.slice(startIndex, endIndex);
                
                return (
                  <div key={slideIndex} className="carousel-slide">
                    {slideComments.map((comment) => (
                      <div key={comment.id} className="comment-card">
                        <div className="comment-card-header">
                          <div className="comment-author">
                            <img
                              src={comment.photoUrl}
                              alt={comment.name}
                              className="comment-author-photo"
                            />
                            <div className="comment-author-info">
                              <h3 className="comment-author-name">{comment.name}</h3>
                              <div className="comment-rating">
                                <StarRating rating={comment.rating} size="small" />
                              </div>
                            </div>
                          </div>
                          <div className="platform-logo-wrapper">
                            {getPlatformLogo(comment.platform)}
                          </div>
                        </div>
                        <div className="comment-text-wrapper">
                          <p className="comment-text">
                            {getDisplayText(comment.text, comment.id)}
                          </p>
                          {isLongText(comment.text) && (
                            <button
                              className="read-more-btn"
                              onClick={() => toggleCommentExpanded(comment.id)}
                              aria-label={expandedComments.has(comment.id) ? 'Read less' : 'Read more'}
                            >
                              {expandedComments.has(comment.id) ? 'Read less' : 'Read more'}
                            </button>
                          )}
                        </div>
                        {comment.commentPhotos && comment.commentPhotos.length > 0 && (
                          <div 
                            className="comment-photos"
                            data-count={comment.commentPhotos.length}
                          >
                            {comment.commentPhotos.slice(0, 3).map((photoUrl, index) => (
                              <img
                                key={index}
                                src={photoUrl}
                                alt={`${comment.name} review ${index + 1}`}
                                className="comment-photo"
                                loading="lazy"
                              />
                            ))}
                          </div>
                        )}
                        <p className="comment-date">{formatDate(comment.date)}</p>
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {totalSlides > 1 && (
          <div className="carousel-indicators">
            {Array.from({ length: totalSlides }).map((_, index) => (
              <button
                key={index}
                className={`carousel-indicator ${index === currentSlide ? 'active' : ''}`}
                onClick={() => goToSlide(index)}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        )}

        <div className="review-buttons">
          <a
            href={reviewLinks.googleUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="review-button review-button-google"
          >
            Review us on Google
          </a>
          <a
            href={reviewLinks.nextdoorUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="review-button review-button-nextdoor"
          >
            Review us on Nextdoor
          </a>
        </div>
      </div>
    </section>
  );
};

export default Comments;

